import { Flex, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { TechnologicalTransfer } from '@/module/Author/TechnologicalTransfer';
import { TechnologicalTransferCard } from '@/module/Author/TechnologicalTransfer/components/Card/TechnologicalTransferCard';
import { CriteriaTechnologicalTransferSelectAddModal } from '@/module/Laboratory/criteria/components/CriteriaTechnologicalTransferSelectAddModal';
import { getCenterReportCriteriaDataOptions } from '@/module/ResearchCenter';

import { NoContentPlaceholder, PendingSpinner } from '@/core/components';

import { Id } from '@feature/utils';

import { CriteriaHeaderButtons } from './components/components/CriteriaHeaderButtons';
import { CriteriaItemDeleteButton } from './components/components/CriteriaItemDeleteButton';
import { useSaveCriteriaData } from './components/hooks/useSaveCriteriaData';

export interface CenterReportTechnologicalTransferSelectProps {
  labId: Id;
  criteriaCode: string;
}

export function CenterReportTechnologicalTransferSelect({
  labId,
  criteriaCode,
}: CenterReportTechnologicalTransferSelectProps) {
  const [opened, { open, close }] = useDisclosure(false);
  const { t } = useTranslation();
  const {
    data: {
      completed,
      technologicalTransfers: { available, selected },
    },
  } = useSuspenseQuery(getCenterReportCriteriaDataOptions(labId, criteriaCode));
  const { onSubmit, isPending } = useSaveCriteriaData(labId, criteriaCode, close);

  const onAdd = (technologicalTransfer: TechnologicalTransfer) => onSubmit({ toAdd: technologicalTransfer });
  const onRemove = (technologicalTransfer: TechnologicalTransfer) => onSubmit({ toRemove: technologicalTransfer });

  return (
    <div>
      <CriteriaHeaderButtons
        completed={completed}
        criteriaCode={criteriaCode}
        labId={labId}
        onAdd={open}
      />
      {opened && (
        <CriteriaTechnologicalTransferSelectAddModal
          available={available}
          selected={selected}
          close={close}
          onAdd={onAdd}
        />
      )}
      <>{isPending && <PendingSpinner />}</>
      {!isPending && (
        <>
          {selected.length > 0 && (
            <Stack>
              {selected.map((technologicalTransfer, index) => (
                <Flex
                  align="center"
                  key={technologicalTransfer.id}
                >
                  {!completed && (
                    <CriteriaItemDeleteButton
                      index={index}
                      onRemove={() => onRemove(technologicalTransfer)}
                    />
                  )}

                  <TechnologicalTransferCard
                    technologicalTransfer={technologicalTransfer}
                    onClick={() => window.open(`/author/technological-transfer/${technologicalTransfer.id}`)}
                  />
                </Flex>
              ))}
            </Stack>
          )}
          {selected.length === 0 && <NoContentPlaceholder title={t('centerReport.noCriteriaContent')} />}
        </>
      )}
    </div>
  );
}
